import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'

import { Button, Form, Input, Space } from 'antd'
import { useForm } from 'antd/lib/form/Form'

import { LockOutlined, MailOutlined } from '@ant-design/icons'

import {
  selectIsAuthenticated,
  selectSignInError,
  selectSignInLoading
} from '@/store/slices/auth/selectors'
import { AppDispatch } from '@/store/store'
import { clearSignInError } from '@store/slices/auth/actions'
import { signIn, signOut } from '@store/slices/auth/thunks'

import { reSendActivationEmail } from '@/services/auth-service'

import '../styles.less'
import React from 'react';

// Utility to parse error message and add hyperlink if $...$ is present
function renderErrorMessage(message: string) {
  if (!message) return null;
  
  // Check if message contains $ symbols (indicating URL)
  const dollarRegex = /\$([^$]+)\$/g;
  const matches = message.match(dollarRegex);
  
  // If no $ symbols, return message as-is
  if (!matches) {
    return <span>{message}</span>;
  }

  // If $ symbols are present, find the URL and remove it, but keep "Schedule a call" text
  let processedMessage = message;
  const urlMatch = message.match(/\$([^$]+)\$/);
  if (urlMatch) {
    const url = urlMatch[0]; // Full match including $ symbols
    const beforeUrl = message.substring(0, message.indexOf(url));
    const afterUrl = message.substring(message.indexOf(url) + url.length);
    
    // Keep everything before the URL and after the URL, but remove the URL itself
    processedMessage = beforeUrl + afterUrl;
  }

  // Split the processed message by $ symbols and process each part
  const parts = processedMessage.split(dollarRegex);
  const result: React.ReactNode[] = [];
  
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      // Regular text part
      if (parts[i]) result.push(<span key={i}>{parts[i]}</span>);
    } else {
      // URL part (between $ symbols) - this shouldn't happen now since we removed URLs
      const url = parts[i];
      if (url) {
        result.push(<span key={i}>{url}</span>);
      }
    }
  }
  
  // Now add the hyperlink for "Schedule a call" if it exists
  const finalResult = [];
  const scheduleText = result.map(node => {
    if (typeof node === 'string' || (node && typeof node === 'object' && 'props' in node)) {
      const text = typeof node === 'string' ? node : node.props?.children;
      if (text && text.includes('Schedule a call')) {
        // Find the URL from the original message
        const urlMatch = message.match(/\$([^$]+)\$/);
        if (urlMatch) {
          const url = urlMatch[1]; // URL without $ symbols
          const parts = text.split('Schedule a call');
          return (
            <>
              {parts[0]}
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1890ff', textDecoration: 'underline' }}
              >
                Schedule a call
              </a>
              {parts[1]}
            </>
          );
        }
      }
    }
    return node;
  });
  
  return <>{scheduleText}</>;
}

const EmailForm = ({ onSuccess }) => {
  const history = useHistory()
  const dispatch: AppDispatch = useDispatch()
  const error = useSelector(selectSignInError)
  const isLoading = useSelector(selectSignInLoading)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const [form] = useForm()
  const [notActivatedError, setNotActivatedError] = useState<string>(null)
  const errors = [error, notActivatedError]
  const signInUser = () => {
    const email = form.getFieldValue('email').toLowerCase()
    const password = form.getFieldValue('password')
    dispatch(
      signIn({
        email,
        password
      })
    ).then(response => {
      if (response?.meta?.requestStatus === 'fulfilled') {
        if (response.payload.active === true) {
          onSuccess({ email, password })
          form.setFieldsValue({
            email: '',
            password: ''
          })
          setNotActivatedError(null)
        } else {
          setNotActivatedError('Your account has not been activated, please, check your email.')
        }
      }
    })
  }

  const resendActivation = () => {
    const email = form.getFieldValue('email').toLowerCase()
    const password = form.getFieldValue('password')
    reSendActivationEmail({ email, password })
    setNotActivatedError(null)
  }

  const onValueChange = () => {
    if (error) {
      dispatch(clearSignInError())
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      history.push('/projects')
    }
  }, [history, isAuthenticated])

  useEffect(() => {
    dispatch(clearSignInError())
  }, [dispatch])

  useEffect(() => {
    dispatch(signOut())
  }, [])

  return (
    <div className="auth__card">
      <div className="card__header">
        <h1>Sign in</h1>
        <p>
          Don't have an account? <Link to="sign-up">Sign up</Link>
        </p>
      </div>

      <div className="card__body">
        <Form
          form={form}
          onValuesChange={onValueChange}
          onFinish={signInUser}
          layout="vertical"
          style={{ width: '85%' }}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 24 }}
          initialValues={{ remember: true }}
          autoComplete="off"
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
            >
              <Input placeholder="Email" prefix={<MailOutlined />} />
            </Form.Item>
            <Form.Item name="password" label="Password" rules={[{ required: true }]}>
              <Input.Password prefix={<LockOutlined />} />
            </Form.Item>

            <p>
              <Link to="forgot-password">Forgot password?</Link>
            </p>

            <Form.Item wrapperCol={{ offset: 4, span: 16 }} style={{ marginTop: '8px' }}>
              <Button type="primary" htmlType="submit" loading={isLoading} block>
                Sign in
              </Button>
            </Form.Item>

            {notActivatedError !== null && (
              <Form.Item wrapperCol={{ offset: 4, span: 16 }}>
                <Button onClick={resendActivation} htmlType="button" block>
                  Resend activation email
                </Button>
              </Form.Item>
            )}

            <Form.Item>
              {errors.map((error, index) => (
                <p key={index} style={{ textAlign: 'center' }}>
                  {renderErrorMessage(error)}
                </p>
              ))}
            </Form.Item>
          </Space>
        </Form>
      </div>
    </div>
  )
}

export default EmailForm